import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Key, Percent, Shield, ExternalLink, Bot, RefreshCw, Database, Sparkles, Copy, RotateCcw, Megaphone, Plus, Trash2, Clock, Calendar } from "lucide-react";
import api from "../lib/api";

const ClaudeBotKeyCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [revealed, setRevealed] = useState(null); // plaintext shown ONCE after rotate

  const fetchStatus = async () => {
    try {
      const res = await api.get("/integrations/claude-bot/status");
      setStatus(res.data);
    } catch (e) {
      console.warn("Claude bot status unavailable:", e?.response?.status);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleRotate = async () => {
    if (status?.configured && !window.confirm("Rotate the Claude bot API key? The previous key will stop working immediately.")) {
      return;
    }
    try {
      setRotating(true);
      const res = await api.post("/integrations/claude-bot/rotate-key");
      setRevealed(res.data.api_key);
      toast.success("New key generated — copy it now (won't be shown again)");
      fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rotate key");
    } finally {
      setRotating(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Revoke the bot key? Auto-repair stops until a new key is generated.")) return;
    try {
      await api.post("/integrations/claude-bot/revoke-key");
      setRevealed(null);
      toast.success("Bot key revoked");
      fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  if (loading) return null;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader>
        <CardTitle className="text-zinc-50 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-400" />
          Claude AI Developer Bot
          {status?.configured ? (
            <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Active</span>
          ) : (
            <span className="text-[11px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">No key</span>
          )}
        </CardTitle>
        <CardDescription className="text-zinc-400">
          API key used by Claude to read the dev backlog, claim items, and auto-mark them shipped after deploy.
          Sent in the <code className="text-zinc-300">X-Internal-API-Key</code> header. Stored as a sha256 hash.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {revealed && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-2">
            <div className="text-xs text-amber-300 font-medium">⚠ Save this key now — it won't be shown again.</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-emerald-300 text-xs font-mono break-all">
                {revealed}
              </code>
              <Button size="sm" variant="outline" className="border-zinc-700" onClick={() => copy(revealed)}>
                <Copy className="w-3.5 h-3.5 mr-1.5" />Copy
              </Button>
            </div>
            <details className="text-[11px] text-zinc-400">
              <summary className="cursor-pointer hover:text-zinc-300">Show curl example</summary>
              <pre className="mt-1 p-2 bg-zinc-950 rounded text-[10px] overflow-x-auto">{`curl -H "X-Internal-API-Key: ${revealed}" https://smartorange.space/api/dev-backlog?status=backlog`}</pre>
            </details>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-zinc-300">
              {status?.configured ? (
                <>Key preview: <code className="text-zinc-100 font-mono">{status.preview}</code></>
              ) : (
                <span className="text-zinc-500 italic">No key configured.</span>
              )}
            </div>
            {status?.created_at && (
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Created {new Date(status.created_at).toLocaleString()} by {status.created_by || "unknown"}
              </div>
            )}
            {status?.bot_user && (
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Maps to staff user: <span className="text-zinc-300">{status.bot_user.name}</span> ({status.bot_user.email})
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRotate} disabled={rotating} className="bg-purple-600 hover:bg-purple-700">
              <RotateCcw className={`w-3.5 h-3.5 mr-1.5 ${rotating ? "animate-spin" : ""}`} />
              {status?.configured ? "Rotate key" : "Generate key"}
            </Button>
            {status?.configured && (
              <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={handleRevoke}>
                Revoke
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const IntegrationSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openAiAdsSyncing, setOpenAiAdsSyncing] = useState(false);
  const [settings, setSettings] = useState({
    stripe_secret_key: "",
    stripe_publishable_key: "",
    stripe_webhook_secret: "",
    stripe_processing_fee_percent: 3.0,
    stripe_enabled: false,
    stripe_secret_key_masked: "",
    stripe_webhook_secret_masked: "",
    crm_partner_api_key: "",
    crm_partner_api_key_masked: "",
    crm_partner_base_url: "",
    meta_ads_api_key: "",
    meta_ads_api_key_masked: "",
    meta_ads_enabled: false,
    tiktok_ads_api_key: "",
    tiktok_ads_api_key_masked: "",
    tiktok_advertiser_id: "",
    openai_ads_api_key: "",
    openai_ads_api_key_masked: "",
    worksection_api_key: "",
    worksection_api_key_masked: "",
    worksection_account: "",
    worksection_enabled: false,
    worksection_sync_floor: "2026-01-01",
    fellow_api_key: "",
    fellow_api_key_masked: "",
    fellow_subdomain: "",
    fellow_workspace_id: "",
    fellow_enabled: false,
    // Spinhouse Roleplay
    anthropic_api_key: "",
    anthropic_api_key_masked: "",
    deepgram_api_key: "",
    deepgram_api_key_masked: "",
    elevenlabs_api_key: "",
    elevenlabs_api_key_masked: "",
  });
  const [crmStatus, setCrmStatus] = useState(null);
  const [crmSyncing, setCrmSyncing] = useState(false);
  const [metaStatus, setMetaStatus] = useState(null);
  const [metaSyncing, setMetaSyncing] = useState(false);
  const [newAdAccountId, setNewAdAccountId] = useState("");
  const [newAdAccountLabel, setNewAdAccountLabel] = useState("");
  const [addingAdAccount, setAddingAdAccount] = useState(false);
  const [wsStatus, setWsStatus] = useState(null);
  const [wsSyncing, setWsSyncing] = useState(false);
  // Fellow.app — test-connection state. Stores last probe response so admin
  // can see what Fellow actually returns and confirm the auth shape. Marker: fellow-transcripts-v1
  const [fellowTesting, setFellowTesting] = useState(false);
  const [fellowProbeResult, setFellowProbeResult] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchCrmStatus();
    fetchMetaStatus();
    fetchWsStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/integrations");
      setSettings(res.data);
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Could not load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchCrmStatus = async () => {
    // Two halves of the same pipeline: the partner mirror (raw rows) and the
    // sales projection the portal actually queries. Shown together because a
    // fresh mirror with a stale projection looks fine in either panel alone.
    try {
      const [mirror, projection] = await Promise.all([
        api.get("/crm-partner/sync/status"),
        api.get("/sales-crm/status").catch(() => ({ data: null })),
      ]);
      setCrmStatus({ ...mirror.data, projection: projection?.data || null });
    } catch (error) {
      // Non-fatal — page still works without status panel.
      console.warn("CRM status unavailable:", error?.response?.status);
    }
  };

  const fetchMetaStatus = async () => {
    try {
      const res = await api.get("/meta-ads/sync/status");
      setMetaStatus(res.data);
    } catch (error) {
      console.warn("Meta Ads status unavailable:", error?.response?.status);
    }
  };

  const fetchWsStatus = async () => {
    try {
      const res = await api.get("/worksection/sync/status");
      setWsStatus(res.data);
    } catch (error) {
      console.warn("Worksection status unavailable:", error?.response?.status);
    }
  };

  const runWsSync = async (full = false) => {
    try {
      setWsSyncing(true);
      const path = full ? "/worksection/sync/full" : "/worksection/sync/incremental";
      const res = await api.post(path);
      const errs = res.data?.errors || [];
      const u = res.data?.users_synced || 0;
      const p = (res.data?.projects_synced || {}).total || 0;
      const t = res.data?.tasks_synced || 0;
      const e = res.data?.entries_synced || 0;
      const elapsed = res.data?.elapsed_sec;
      const tail = elapsed ? ` (${elapsed}s)` : "";
      if (errs.length) {
        toast.warning(`Sync finished with ${errs.length} error(s) · ${u} users · ${p} projects · ${t} tasks · ${e} entries${tail}`);
      } else {
        toast.success(`Sync OK · ${u} users · ${p} projects · ${t} tasks · ${e} entries${tail}`);
      }
      fetchWsStatus();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Worksection sync failed";
      toast.error(detail);
    } finally {
      setWsSyncing(false);
    }
  };

  const runMetaSync = async (full = false) => {
    try {
      setMetaSyncing(true);
      const path = full ? "/meta-ads/sync/full" : "/meta-ads/sync/incremental";
      const res = await api.post(path);
      const errs = res.data?.errors || [];
      const accts = res.data?.accounts || [];
      const totalInsights = accts.reduce((a, b) => a + (b?.insights || 0), 0);
      if (errs.length) {
        toast.warning(`Sync finished with ${errs.length} error(s). ${totalInsights} insight rows upserted.`);
      } else {
        toast.success(`Sync OK: ${totalInsights} insight rows upserted across ${accts.length} account(s).`);
      }
      fetchMetaStatus();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Sync failed";
      toast.error(detail);
    } finally {
      setMetaSyncing(false);
    }
  };

  const addAdAccount = async () => {
    const id = (newAdAccountId || "").trim().replace(/^act_/, "");
    if (!id || !/^\d+$/.test(id)) {
      toast.error("Ad Account ID must be numeric (with or without act_ prefix)");
      return;
    }
    try {
      setAddingAdAccount(true);
      await api.post("/meta-ads/accounts", {
        ad_account_id: id,
        label: newAdAccountLabel.trim(),
      });
      toast.success("Ad account added");
      setNewAdAccountId("");
      setNewAdAccountLabel("");
      fetchMetaStatus();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Could not add account";
      toast.error(detail);
    } finally {
      setAddingAdAccount(false);
    }
  };

  const removeAdAccount = async (adAccountId) => {
    if (!window.confirm(`Remove ad account ${adAccountId}? Historical data stays in Mongo, only sync stops.`)) return;
    try {
      await api.delete(`/meta-ads/accounts/${adAccountId}`);
      toast.success("Ad account removed");
      fetchMetaStatus();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Could not remove account";
      toast.error(detail);
    }
  };

  const toggleAdAccount = async (adAccountId, isActive) => {
    try {
      await api.put(`/meta-ads/accounts/${adAccountId}`, { is_active: isActive });
      fetchMetaStatus();
    } catch (error) {
      toast.error("Could not toggle account");
    }
  };

  const runCrmSync = async (full = false) => {
    // Partner sync is synchronous on their side (a full pass over 19 resources
    // took ~4 min at 62k rows) and rebuilds the sales projection when it ends,
    // so one await covers both halves — no polling loop needed.
    setCrmSyncing(true);
    try {
      const res = await api.post(full ? "/crm-partner/sync/full" : "/crm-partner/sync/incremental");
      const totals = res.data?.totals || {};
      const projected = res.data?.projection?.counts?.sales_deals;
      toast.success(
        `${full ? "Full" : "Incremental"} sync done · ${totals.upserted ?? 0} rows` +
        (projected ? ` · ${projected} deals projected` : "")
      );
      if ((res.data?.errors || []).length) {
        toast.warning(`${res.data.errors.length} resource(s) reported errors — see status panel`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to run sync");
    } finally {
      setCrmSyncing(false);
      fetchCrmStatus();
    }
  };

  const rebuildProjection = async () => {
    setCrmSyncing(true);
    try {
      const res = await api.post("/sales-crm/rebuild");
      const c = res.data?.counts || {};
      toast.success(`Projection rebuilt · ${c.sales_deals ?? 0} deals, ${c.sales_contacts ?? 0} contacts`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to rebuild projection");
    } finally {
      setCrmSyncing(false);
      fetchCrmStatus();
    }
  };

  // The page is long and has a single save button near the top, so a card far
  // below is easy to miss entirely. A hash link lands straight on it.
  useEffect(() => {
    if (!window.location.hash) return;
    const el = document.querySelector(window.location.hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, []);

  const syncOpenAiAds = async () => {
    setOpenAiAdsSyncing(true);
    try {
      const { data } = await api.post("/openai-ads/sync/full");
      const errs = (data.errors || []).join("; ");
      if (errs) toast.error(`OpenAI Ads: ${errs}`);
      else toast.success(`OpenAI Ads: подтянуто строк — ${data.insights}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Не удалось подтянуть расход OpenAI");
    } finally {
      setOpenAiAdsSyncing(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put("/settings/integrations", {
        stripe_secret_key: settings.stripe_secret_key,
        stripe_publishable_key: settings.stripe_publishable_key,
        stripe_webhook_secret: settings.stripe_webhook_secret,
        stripe_processing_fee_percent: settings.stripe_processing_fee_percent,
        stripe_enabled: settings.stripe_enabled,
        crm_partner_api_key: settings.crm_partner_api_key,
        crm_partner_base_url: settings.crm_partner_base_url,
        meta_ads_api_key: settings.meta_ads_api_key,
        meta_ads_enabled: settings.meta_ads_enabled,
        tiktok_ads_api_key: settings.tiktok_ads_api_key,
        tiktok_advertiser_id: settings.tiktok_advertiser_id,
        openai_ads_api_key: settings.openai_ads_api_key,
        worksection_api_key: settings.worksection_api_key,
        worksection_account: settings.worksection_account,
        worksection_enabled: settings.worksection_enabled,
        worksection_sync_floor: settings.worksection_sync_floor,
        fellow_api_key: settings.fellow_api_key,
        fellow_subdomain: settings.fellow_subdomain,
        fellow_workspace_id: settings.fellow_workspace_id,
        fellow_enabled: settings.fellow_enabled,
        anthropic_api_key: settings.anthropic_api_key,
        deepgram_api_key: settings.deepgram_api_key,
        elevenlabs_api_key: settings.elevenlabs_api_key,
      });
      toast.success("Settings saved");
      fetchSettings();
      fetchCrmStatus();
      fetchMetaStatus();
      fetchWsStatus();
    } catch (error) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50">Integrations</h1>
          <p className="text-zinc-400 text-sm mt-1">Payment systems and external service configuration</p>
        </div>
      </div>

      {/* Stripe Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <CreditCard className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-zinc-50">Stripe</CardTitle>
                <CardDescription>Card payments for TRP invoices</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="stripe-enabled" className="text-zinc-400 text-sm">Enabled</Label>
              <Switch
                id="stripe-enabled"
                checked={settings.stripe_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, stripe_enabled: checked })}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* API Keys */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Key className="h-4 w-4" />
                Secret Key
              </Label>
              <Input
                type="password"
                placeholder={settings.stripe_secret_key_masked || "sk_live_..."}
                value={settings.stripe_secret_key}
                onChange={(e) => setSettings({ ...settings, stripe_secret_key: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">Starts with sk_live_ or sk_test_</p>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Key className="h-4 w-4" />
                Publishable Key
              </Label>
              <Input
                type="text"
                placeholder="pk_live_..."
                value={settings.stripe_publishable_key}
                onChange={(e) => setSettings({ ...settings, stripe_publishable_key: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">Starts with pk_live_ or pk_test_</p>
            </div>
          </div>

          {/* Webhook Secret */}
          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Webhook Secret
            </Label>
            <Input
              type="password"
              placeholder={settings.stripe_webhook_secret_masked || "whsec_..."}
              value={settings.stripe_webhook_secret}
              onChange={(e) => setSettings({ ...settings, stripe_webhook_secret: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-zinc-50 font-mono text-sm"
            />
            <p className="text-xs text-zinc-500">
              Create a webhook in{" "}
              <a
                href="https://dashboard.stripe.com/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:underline inline-flex items-center gap-1"
              >
                Stripe Dashboard <ExternalLink className="h-3 w-3" />
              </a>
              {" "}with URL: <code className="bg-zinc-800 px-1 rounded">https://smartorange.space/api/stripe/webhook</code>
            </p>
          </div>

          {/* Processing Fee */}
          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2">
              <Percent className="h-4 w-4" />
              Processing Fee (%)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={settings.stripe_processing_fee_percent}
                onChange={(e) => setSettings({ ...settings, stripe_processing_fee_percent: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 w-24"
              />
              <span className="text-zinc-400">%</span>
              <span className="text-xs text-zinc-500 ml-2">
                Added to invoice total as &quot;Processing Fee&quot;
              </span>
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
            <h4 className="text-sm font-medium text-purple-300 mb-2">How it works:</h4>
            <ul className="text-xs text-zinc-400 space-y-1">
              <li>• Stripe payment link is generated when a TRP invoice is created</li>
              <li>• Partner sees the list of services + Processing Fee ({settings.stripe_processing_fee_percent}%)</li>
              <li>• Payment status auto-updates in the system after the transaction</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700"
        >
          {saving ? "Saving..." : "Save settings"}
        </Button>
      </div>

      {/* Claude (Anthropic) Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Bot className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-zinc-50">Claude (Anthropic) API</CardTitle>
              <CardDescription className="text-zinc-400">
                One key for every AI feature of the portal
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2"><Key className="w-4 h-4" /> API key</Label>
            <Input type="password" placeholder={settings.anthropic_api_key_masked || "sk-ant-..."}
                   value={settings.anthropic_api_key}
                   onChange={(e) => setSettings({ ...settings, anthropic_api_key: e.target.value })}
                   className="bg-zinc-800 border-zinc-700" />
            <p className="text-xs text-zinc-500">
              Get a key at{" "}
              <a
                href="https://platform.claude.com/settings/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:underline inline-flex items-center gap-1"
              >
                platform.claude.com <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-lg">
            <h4 className="text-sm font-medium text-orange-300 mb-2">How it works:</h4>
            <ul className="text-xs text-zinc-400 space-y-1">
              <li>
                • Powers AI Support Chat,{" "}
                <a href="/settings/ai-qc" className="text-orange-400 hover:underline">
                  AI Quality Control
                </a>
                , the project setup chat, marketing lead analysis and the sales trainer
              </li>
              <li>• If ANTHROPIC_API_KEY is set in the server's backend/.env, it takes precedence over this field</li>
              <li>• If no key is set anywhere — these features are unavailable</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Spinhouse Roleplay Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Bot className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-zinc-50">Spinhouse Roleplay</CardTitle>
              <CardDescription className="text-zinc-400">
                Keys for the sales trainer — speech-to-text and text-to-speech (the buyer dialog
                uses the Claude key above). Leave empty to fall back to browser speech.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2"><Key className="w-4 h-4" /> Deepgram API key (STT)</Label>
            <Input type="password" placeholder={settings.deepgram_api_key_masked || "…"}
                   value={settings.deepgram_api_key}
                   onChange={(e) => setSettings({ ...settings, deepgram_api_key: e.target.value })}
                   className="bg-zinc-800 border-zinc-700" />
            <p className="text-xs text-zinc-500">console.deepgram.com → API Keys. Speech recognition (tr/ru/en/uk).</p>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-2"><Key className="w-4 h-4" /> ElevenLabs API key (TTS)</Label>
            <Input type="password" placeholder={settings.elevenlabs_api_key_masked || "sk_..."}
                   value={settings.elevenlabs_api_key}
                   onChange={(e) => setSettings({ ...settings, elevenlabs_api_key: e.target.value })}
                   className="bg-zinc-800 border-zinc-700" />
            <p className="text-xs text-zinc-500">elevenlabs.io → API Keys. Natural buyer voice.</p>
          </div>
        </CardContent>
      </Card>

      {/* Sales CRM (partner mirror) Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-zinc-50">Sales CRM (crm.smartorange.space)</CardTitle>
              <CardDescription className="text-zinc-400">
                Read-only mirror of the partner CRM + the normalized sales projection the portal queries
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="crm_partner_api_key" className="text-zinc-300 flex items-center gap-2">
              <Key className="w-3.5 h-3.5" /> Partner API key
            </Label>
            <Input
              id="crm_partner_api_key"
              type="password"
              placeholder={settings.crm_partner_api_key_masked || "set via CRM_PARTNER_API_KEY env"}
              value={settings.crm_partner_api_key || ""}
              onChange={(e) => setSettings({ ...settings, crm_partner_api_key: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-zinc-100"
            />
            <Label htmlFor="crm_partner_base_url" className="text-zinc-300">Base URL</Label>
            <Input
              id="crm_partner_base_url"
              placeholder="https://crm.smartorange.space"
              value={settings.crm_partner_base_url || ""}
              onChange={(e) => setSettings({ ...settings, crm_partner_base_url: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-zinc-100"
            />
            <p className="text-xs text-zinc-500">
              Leave blank to keep the values already set through the environment on the server.
            </p>
          </div>

          {/* Status panel */}
          {crmStatus && (
            <div className="p-3 bg-zinc-950/40 border border-zinc-800 rounded-lg space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Connection:</span>
                <span className={crmStatus.configured ? "text-emerald-400" : "text-amber-400"}>
                  {crmStatus.configured ? `configured · ${crmStatus.base_url || ""}` : "key / base URL not configured"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Last sync:</span>
                <span className="text-zinc-300">
                  {crmStatus.last_incremental_at ? new Date(crmStatus.last_incremental_at).toLocaleString() : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Projection rebuilt:</span>
                <span className="text-zinc-300">
                  {crmStatus.projection?.last_run?.finished_at
                    ? `${new Date(crmStatus.projection.last_run.finished_at).toLocaleString()} · ${crmStatus.projection.last_run.duration_sec}s`
                    : "—"}
                </span>
              </div>
              {crmStatus.projection?.counts && (
                <div className="grid grid-cols-4 gap-2 pt-2 border-t border-zinc-800">
                  {[
                    ["sales_deals", "Deals"],
                    ["sales_contacts", "Contacts"],
                    ["sales_companies", "Companies"],
                    ["sales_call_scores", "Call scores"],
                  ].map(([key, label]) => (
                    <div key={key} className="text-center">
                      <div className="text-zinc-500">{label}</div>
                      <div className="text-zinc-200 font-mono">{crmStatus.projection.counts?.[key] ?? 0}</div>
                    </div>
                  ))}
                </div>
              )}
              {crmStatus.projection?.last_run?.attribution && (
                <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                  <span className="text-zinc-400">Deal attribution source:</span>
                  <span className="text-zinc-300 font-mono">
                    {crmStatus.projection.last_run.attribution.fields} columns ·{" "}
                    {crmStatus.projection.last_run.attribution.description} parsed ·{" "}
                    {crmStatus.projection.last_run.attribution.none} none
                  </span>
                </div>
              )}
              {(crmStatus.last_summary?.errors || []).length > 0 && (
                <div className="pt-2 border-t border-zinc-800">
                  <div className="text-amber-400 mb-1">Recent errors:</div>
                  {crmStatus.last_summary.errors.slice(0, 3).map((er, i) => (
                    <div key={i} className="text-zinc-500 truncate">• {er.step}: {String(er.detail).slice(0, 120)}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              disabled={crmSyncing}
              onClick={() => runCrmSync(false)}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${crmSyncing ? "animate-spin" : ""}`} />
              Sync (incremental)
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={crmSyncing}
              onClick={() => runCrmSync(true)}
              className="border-zinc-700"
            >
              Full re-sync
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={crmSyncing}
              onClick={rebuildProjection}
              className="border-zinc-700"
            >
              Rebuild projection
            </Button>
            <span className="text-xs text-zinc-500">Auto-sync every 30 minutes.</span>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-lg">
            <h4 className="text-sm font-medium text-orange-300 mb-2">How it works:</h4>
            <ul className="text-xs text-zinc-400 space-y-1">
              <li>• The CRM owns sales; the portal mirrors it read-only and never writes back</li>
              <li>• Raw rows land in crm_partner_records, then project into sales_deals / sales_contacts / …</li>
              <li>• Analytics (Marketing, Ads Quality, Dashboard pipeline) read the projection, not the CRM</li>
              <li>• Deal → project links live on the portal side, in sales_deal_links</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Meta Ads Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-lg text-zinc-50">Meta Ads</CardTitle>
                <CardDescription className="text-zinc-400">
                  Read-only mirror of Facebook / Instagram ad spend, joined to leads via utm_campaign
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="meta-ads-enabled" className="text-zinc-400 text-sm">
                {settings.meta_ads_enabled ? "Enabled" : "Disabled"}
              </Label>
              <Switch
                id="meta-ads-enabled"
                checked={!!settings.meta_ads_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, meta_ads_enabled: checked })}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Token */}
          <div className="space-y-2">
            <Label htmlFor="meta_ads_api_key" className="text-zinc-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              System User Access Token
            </Label>
            <Input
              id="meta_ads_api_key"
              type="password"
              placeholder={settings.meta_ads_api_key_masked || "EAA…"}
              value={settings.meta_ads_api_key}
              onChange={(e) => setSettings({ ...settings, meta_ads_api_key: e.target.value })}
              className="bg-zinc-800 border-zinc-700 font-mono text-sm"
            />
            <p className="text-xs text-zinc-500">
              Create a System User in{" "}
              <a
                href="https://business.facebook.com/settings/system-users"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                Meta Business Manager <ExternalLink className="w-3 h-3" />
              </a>{" "}
              with <code className="bg-zinc-800 px-1 rounded">ads_read</code> scope. Set token expiration to{" "}
              <strong>Never</strong>. Assign your Ad Accounts to the System User with{" "}
              <code className="bg-zinc-800 px-1 rounded">View Performance</code> permission. Click &quot;Save&quot; below
              before adding Ad Accounts.
            </p>
          </div>

          {/* Status panel */}
          {metaStatus && (
            <div className="p-3 bg-zinc-950/40 border border-zinc-800 rounded-lg space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Token:</span>
                <span className={metaStatus.token_configured ? "text-emerald-400" : "text-amber-400"}>
                  {metaStatus.token_configured ? "configured" : "not configured"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Last incremental sync:</span>
                <span className="text-zinc-300">
                  {metaStatus.last_incremental_at
                    ? new Date(metaStatus.last_incremental_at).toLocaleString()
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Full re-sync:</span>
                <span className="text-zinc-300">
                  {metaStatus.last_full_at ? new Date(metaStatus.last_full_at).toLocaleString() : "—"}
                </span>
              </div>
              <div className="grid grid-cols-5 gap-2 pt-2 border-t border-zinc-800">
                {[
                  ["accounts", "Accounts"],
                  ["campaigns", "Campaigns"],
                  ["adsets", "Adsets"],
                  ["ads", "Ads"],
                  ["insights_rows", "Insight rows"],
                ].map(([key, label]) => (
                  <div key={key} className="text-center">
                    <div className="text-zinc-500">{label}</div>
                    <div className="text-zinc-200 font-mono">{metaStatus.counts?.[key] ?? 0}</div>
                  </div>
                ))}
              </div>
              {(metaStatus.last_summary?.errors || []).length > 0 && (
                <div className="pt-2 border-t border-zinc-800">
                  <div className="text-amber-400 mb-1">Recent errors:</div>
                  {metaStatus.last_summary.errors.slice(0, 3).map((er, i) => (
                    <div key={i} className="text-zinc-500 truncate">
                      • {er.ad_account_id ? `act_${er.ad_account_id}: ` : ""}
                      {String(er.detail || "").slice(0, 140)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Ad Accounts list + add form */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-sm">Ad Accounts</Label>
            {(metaStatus?.accounts || []).length === 0 && (
              <div className="text-xs text-zinc-500 italic px-1">
                No ad accounts registered yet. Save your token above first, then add an Ad Account ID below.
              </div>
            )}
            {(metaStatus?.accounts || []).map((acct) => (
              <div
                key={acct.ad_account_id}
                className="flex items-center justify-between p-2 bg-zinc-950/40 border border-zinc-800 rounded text-xs"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Switch
                    checked={!!acct.is_active}
                    onCheckedChange={(checked) => toggleAdAccount(acct.ad_account_id, checked)}
                  />
                  <div className="min-w-0">
                    <div className="text-zinc-200 truncate">
                      {acct.label || acct.name || acct.ad_account_id}
                    </div>
                    <div className="text-zinc-500 font-mono">
                      act_{acct.ad_account_id}
                      {acct.currency ? ` · ${acct.currency}` : ""}
                      {acct.timezone_name ? ` · ${acct.timezone_name}` : ""}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAdAccount(acct.ad_account_id)}
                  className="text-zinc-500 hover:text-red-400"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-1">
              <Input
                placeholder="Ad Account ID (e.g. 1234567890123456)"
                value={newAdAccountId}
                onChange={(e) => setNewAdAccountId(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-xs font-mono flex-1"
              />
              <Input
                placeholder="Label (e.g. TR)"
                value={newAdAccountLabel}
                onChange={(e) => setNewAdAccountLabel(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-xs w-32"
              />
              <Button
                size="sm"
                onClick={addAdAccount}
                disabled={addingAdAccount || !newAdAccountId.trim()}
                className="bg-blue-600 hover:bg-blue-500"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Find the ID in Ads Manager URL (after <code className="bg-zinc-800 px-1 rounded">act=</code>) or in
              account settings. Numeric only — strip the <code className="bg-zinc-800 px-1 rounded">act_</code> prefix
              if present.
            </p>
          </div>

          {/* Sync controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={metaSyncing || !metaStatus?.token_configured}
              onClick={() => runMetaSync(false)}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${metaSyncing ? "animate-spin" : ""}`} />
              Sync (last 30 days)
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={metaSyncing || !metaStatus?.token_configured}
              onClick={() => runMetaSync(true)}
              className="border-zinc-700"
            >
              Full re-sync (365 days)
            </Button>
            <span className="text-xs text-zinc-500">Auto-sync every 6 hours.</span>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <h4 className="text-sm font-medium text-blue-300 mb-2">How this works:</h4>
            <ul className="text-xs text-zinc-400 space-y-1">
              <li>• Daily ad-level insights pull every 6h: spend, impressions, clicks, leads, CPL, CPM, CTR</li>
              <li>
                • Last 30 days are re-fetched on each tick (Meta&apos;s 7-day attribution window means recent numbers
                shift)
              </li>
              <li>
                • JOIN to leads via <code className="bg-zinc-800 px-1 rounded">utm_campaign</code> — make sure your
                ads have URL parameters template{" "}
                <code className="bg-zinc-800 px-1 rounded">
                  utm_source=facebook&amp;utm_medium=paid&amp;utm_campaign={"{{campaign.name}}"}
                </code>
              </li>
              <li>• Read access: admin, sales_director, crm_manager, sales, pm_lead</li>
              <li>• Write access (sync, accounts): admin, sales_director</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Ad platforms beyond Meta. Without these the leads page can price a
          Meta lead and nothing else, and a channel with no spend data reads as
          a free channel. */}
      <Card id="ads-spend" className="bg-zinc-900/50 border-zinc-800 scroll-mt-6">
        <CardHeader>
          <CardTitle className="text-lg text-zinc-50">Расход по рекламе — TikTok и OpenAI</CardTitle>
          <CardDescription className="text-zinc-400">
            Нужны, чтобы на странице «Лиды и воронка» считалась цена лида по этим каналам.
            Без ключа канал показывает «нет данных», а не ноль.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="openai_ads_api_key" className="text-zinc-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              OpenAI Ads API key
            </Label>
            <Input
              id="openai_ads_api_key"
              type="password"
              placeholder={settings.openai_ads_api_key_masked || "ключ из OpenAI Ads Manager"}
              value={settings.openai_ads_api_key}
              onChange={(e) => setSettings({ ...settings, openai_ads_api_key: e.target.value })}
              className="bg-zinc-800 border-zinc-700 font-mono text-sm"
            />
            <p className="text-xs text-zinc-500">
              Это <strong>не</strong> ключ от модельного API. Отдельный ключ берётся в{" "}
              <a href="https://ads.openai.com" target="_blank" rel="noopener noreferrer"
                 className="text-blue-400 hover:underline inline-flex items-center gap-1">
                OpenAI Ads Manager <ExternalLink className="w-3 h-3" />
              </a>{" "}
              → API keys, и привязан к одному рекламному аккаунту. После сохранения расход
              подтянется в течение 6 часов или сразу через{" "}
              <code className="bg-zinc-800 px-1 rounded">POST /api/openai-ads/sync/full</code>.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tiktok_ads_api_key" className="text-zinc-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              TikTok Ads — токен и advertiser ID
            </Label>
            <Input
              id="tiktok_ads_api_key"
              type="password"
              placeholder={settings.tiktok_ads_api_key_masked || "задан в окружении VPS"}
              value={settings.tiktok_ads_api_key}
              onChange={(e) => setSettings({ ...settings, tiktok_ads_api_key: e.target.value })}
              className="bg-zinc-800 border-zinc-700 font-mono text-sm"
            />
            <Input
              id="tiktok_advertiser_id"
              placeholder="advertiser ID, например 7671397218380890129"
              value={settings.tiktok_advertiser_id}
              onChange={(e) => setSettings({ ...settings, tiktok_advertiser_id: e.target.value })}
              className="bg-zinc-800 border-zinc-700 font-mono text-sm"
            />
            <p className="text-xs text-zinc-500">
              Уже заданы в окружении VPS — заполнять здесь нужно только чтобы поменять их
              без передеплоя. Значение отсюда имеет приоритет над окружением.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
              {saving ? "Сохраняю…" : "Сохранить ключи"}
            </Button>
            <Button variant="outline" onClick={syncOpenAiAds} disabled={openAiAdsSyncing}>
              {openAiAdsSyncing ? "Тяну расход…" : "Подтянуть расход OpenAI"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Worksection Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <CardTitle className="text-lg text-zinc-50">Worksection (time tracking)</CardTitle>
                <CardDescription className="text-zinc-400">
                  Read-only mirror of project hours logged by salaried team (designers, developers, testers) — fills{" "}
                  <code className="bg-zinc-800 px-1 rounded text-zinc-300">task.staff_cost_total</code> automatically (Phase 3)
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="ws-enabled" className="text-zinc-400 text-sm">
                {settings.worksection_enabled ? "Enabled" : "Disabled"}
              </Label>
              <Switch
                id="ws-enabled"
                checked={!!settings.worksection_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, worksection_enabled: checked })}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Account subdomain */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="worksection_account" className="text-zinc-300 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Account subdomain
              </Label>
              <Input
                id="worksection_account"
                placeholder="sma8646"
                value={settings.worksection_account || ""}
                onChange={(e) => setSettings({ ...settings, worksection_account: e.target.value.trim() })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">
                Just the subdomain (e.g. <code className="bg-zinc-800 px-1 rounded">sma8646</code>) — full host is{" "}
                <code className="bg-zinc-800 px-1 rounded">{settings.worksection_account || "your-account"}.worksection.com</code>
              </p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="worksection_api_key" className="text-zinc-300 flex items-center gap-2">
                <Key className="w-4 h-4" />
                API key
              </Label>
              <Input
                id="worksection_api_key"
                type="password"
                placeholder={settings.worksection_api_key_masked || "32-char hex from Account settings → API"}
                value={settings.worksection_api_key || ""}
                onChange={(e) => setSettings({ ...settings, worksection_api_key: e.target.value })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">
                In Worksection: <strong>Account settings → API</strong>. Read-only access is sufficient. Click <strong>Save</strong>{" "}
                before triggering sync.
              </p>
            </div>
          </div>

          {/* Sync floor */}
          <div className="space-y-2">
            <Label htmlFor="worksection_sync_floor" className="text-zinc-300 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Sync since (hard floor)
            </Label>
            <Input
              id="worksection_sync_floor"
              type="date"
              value={settings.worksection_sync_floor || "2026-01-01"}
              onChange={(e) => setSettings({ ...settings, worksection_sync_floor: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-sm w-48"
            />
            <p className="text-xs text-zinc-500">
              We never pull WS time-entries older than this date. Default <code className="bg-zinc-800 px-1 rounded">2026-01-01</code> —
              portal is 3 months old and older entries have no portal counterpart.
            </p>
          </div>

          {/* Status panel */}
          {wsStatus && (
            <div className="p-3 bg-zinc-950/40 border border-zinc-800 rounded-lg space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Token:</span>
                <span className={wsStatus.token_configured ? "text-emerald-400" : "text-amber-400"}>
                  {wsStatus.token_configured ? "configured" : "not configured"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Account:</span>
                <span className="text-zinc-300 font-mono">
                  {wsStatus.account ? `${wsStatus.account}.worksection.com` : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Last incremental:</span>
                <span className="text-zinc-300">
                  {wsStatus.last_incremental_at ? new Date(wsStatus.last_incremental_at).toLocaleString() : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Full re-sync:</span>
                <span className="text-zinc-300">
                  {wsStatus.last_full_at ? new Date(wsStatus.last_full_at).toLocaleString() : "—"}
                </span>
              </div>
              <div className="grid grid-cols-6 gap-2 pt-2 border-t border-zinc-800">
                {[
                  ["users", "Users"],
                  ["projects", "Projects"],
                  ["active_projects", "Active"],
                  ["tasks", "Tasks"],
                  ["time_entries", "Entries"],
                  ["user_links", "Linked"],
                ].map(([key, label]) => (
                  <div key={key} className="text-center">
                    <div className="text-zinc-500">{label}</div>
                    <div className="text-zinc-200 font-mono">{wsStatus.counts?.[key] ?? 0}</div>
                  </div>
                ))}
              </div>
              {(wsStatus.last_summary?.errors || []).length > 0 && (
                <div className="pt-2 border-t border-zinc-800">
                  <div className="text-amber-400 mb-1">Recent errors:</div>
                  {wsStatus.last_summary.errors.slice(0, 3).map((er, i) => (
                    <div key={i} className="text-zinc-500 truncate">
                      • {er.step}{er.ws_project_id ? ` (proj ${er.ws_project_id})` : ""}: {String(er.detail || "").slice(0, 140)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Account-wide hours overview (top projects by time, money NOT shown — WS rates are fiction) */}
          {wsStatus?.account_overview?.available && (
            <div className="p-3 bg-zinc-950/40 border border-zinc-800 rounded-lg space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-300 font-medium">Account hours overview</span>
                <span className="text-zinc-500">
                  {Math.round(wsStatus.account_overview.total_hours || 0).toLocaleString()}h total across{" "}
                  {wsStatus.account_overview.projects_count} projects · live from Worksection
                </span>
              </div>
              <div className="text-[11px] text-zinc-500 italic">
                Top projects since {wsStatus.sync_floor || "2026-01-01"}. Money column intentionally hidden — Worksection rates are
                not configured to match real labor cost.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                {(wsStatus.account_overview.top_projects || []).slice(0, 20).map((p) => (
                  <div key={p.ws_project_id} className="flex items-center justify-between text-[11px] px-2 py-1 bg-zinc-900/40 rounded">
                    <span className="text-zinc-300 truncate min-w-0 flex-1" title={p.name}>
                      {p.name || `Project ${p.ws_project_id}`}
                    </span>
                    <span className="text-zinc-400 font-mono whitespace-nowrap ml-2">
                      {Math.round(p.hours)}h
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sync controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={wsSyncing || !wsStatus?.token_configured}
              onClick={() => runWsSync(false)}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${wsSyncing ? "animate-spin" : ""}`} />
              Sync (last 30 days)
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={wsSyncing || !wsStatus?.token_configured}
              onClick={() => runWsSync(true)}
              className="border-zinc-700"
            >
              Full re-sync (since floor)
            </Button>
            <a
              href="/settings/worksection/users"
              className="text-xs text-emerald-400 hover:underline ml-2"
            >
              → Map users to portal staff
            </a>
            <span className="text-xs text-zinc-500 ml-auto">Auto-sync daily at 04:00 UTC.</span>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
            <h4 className="text-sm font-medium text-emerald-300 mb-2">How this works (Phase 1):</h4>
            <ul className="text-xs text-zinc-400 space-y-1">
              <li>• Daily sync at 04:00 UTC: WS projects + users + tasks + time entries → Mongo mirror</li>
              <li>• Only hours are trusted — WS&apos;s money/rate field is ignored (it&apos;s fiction). Cost = hours × <code className="bg-zinc-800 px-1 rounded">monthly_salary / 160</code></li>
              <li>• Phase 2 (next): manually link WS projects ↔ portal projects + WS tasks ↔ portal tasks via fuzzy suggestions</li>
              <li>• Phase 3: auto-write resolved cost to <code className="bg-zinc-800 px-1 rounded">task.staff_cost_total</code> when WS task closes</li>
              <li>• Phase 4: profit/margin overview with full breakdown (revenue − artist − AD − salaried team)</li>
              <li>• Read access: admin, pm_lead, pm, finance · Write (sync, links): admin, pm_lead</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Fellow.app Card — meeting transcripts (Phase 1: token + test connection) */}
      {/* Marker: fellow-transcripts-v1 */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-500/10 rounded-lg">
                <Calendar className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <CardTitle className="text-lg text-zinc-50">Fellow.app (meeting transcripts)</CardTitle>
                <CardDescription className="text-zinc-400 text-xs">
                  Pull full meeting transcripts (verbatim dialogue, not just the bullet summary Fellow shares)
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.fellow_enabled || false}
              onCheckedChange={(checked) => setSettings({ ...settings, fellow_enabled: checked })}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-rose-500/5 border border-rose-500/20 rounded p-3 text-xs text-rose-200/80">
            <div className="font-medium text-rose-300 mb-1">Why this exists</div>
            <p>
              Fellow shares only a bullet-point SUMMARY with connected tools — the verbatim transcript stays in Fellow. To feed real dialogue into our AI lead-quality prompt (instead of bullet recaps), we pull the full transcript via Fellow's API.
            </p>
          </div>

          <div className="bg-zinc-950/60 border border-zinc-800 rounded p-3 text-xs text-zinc-300 space-y-2">
            <div className="font-semibold text-amber-300 flex items-center gap-1.5">
              <Key className="w-3 h-3" /> How to get the API token (Fellow hides it)
            </div>
            <ol className="list-decimal ml-5 space-y-1.5 leading-snug">
              <li>
                <strong className="text-zinc-100">Workspace admin first enables API</strong> (one-time, required — without this the next step won't show anything):
                <div className="text-zinc-400 mt-0.5">
                  Avatar → <strong className="text-zinc-200">Workspace Settings</strong> → <strong className="text-zinc-200">Security</strong> → enable <strong className="text-zinc-200">Developer API</strong>
                </div>
              </li>
              <li>
                <strong className="text-zinc-100">Generate personal token:</strong>
                <div className="text-zinc-400 mt-0.5">
                  Avatar → <strong className="text-zinc-200">Settings</strong> (personal, not workspace) → scroll sidebar to <strong className="text-zinc-200">Developer API</strong> → button top-right → name it <code className="text-zinc-200">smartorange-hub</code> → Generate → <strong className="text-amber-300">copy immediately</strong> (shown once)
                </div>
              </li>
              <li>
                <strong className="text-zinc-100">Paste here</strong> + set subdomain = <code className="text-emerald-300">smarto</code> (we already detected it from your workspace).
              </li>
            </ol>
            <div className="text-[10px] text-zinc-500 italic pt-1">
              Paid plan only (Free tier doesn't get API). If "Developer API" section doesn't appear in your Settings — step 1 wasn't done.
            </div>
          </div>

          <div>
            <Label htmlFor="fellow_subdomain" className="text-zinc-300">Workspace subdomain</Label>
            <Input
              id="fellow_subdomain"
              placeholder="smarto"
              value={settings.fellow_subdomain || ""}
              onChange={(e) => setSettings({ ...settings, fellow_subdomain: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1 font-mono"
            />
            <p className="text-xs text-zinc-500 mt-1">
              The part before <code>.fellow.app</code> in your URL. Yours is <code className="text-emerald-300">smarto</code> (verified via Fellow MCP).
            </p>
          </div>

          <div>
            <Label htmlFor="fellow_api_key" className="text-zinc-300 flex items-center gap-2">
              API Token
              {settings.fellow_api_key_masked && (
                <span className="text-xs text-zinc-500">(saved: {settings.fellow_api_key_masked})</span>
              )}
            </Label>
            <Input
              id="fellow_api_key"
              type="password"
              placeholder={settings.fellow_api_key_masked || "Personal access token from Settings → Developer API (see steps above)"}
              value={settings.fellow_api_key || ""}
              onChange={(e) => setSettings({ ...settings, fellow_api_key: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1"
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={fellowTesting || !settings.fellow_api_key_masked}
              onClick={async () => {
                setFellowTesting(true);
                setFellowProbeResult(null);
                try {
                  const res = await api.post("/integrations/fellow/test-connection");
                  setFellowProbeResult(res.data);
                  if (res.data?.authenticated) {
                    toast.success("Fellow API authenticated — base URL: " + (res.data.base_url || ""));
                  } else {
                    toast.warning(res.data?.diagnosis || "Auth failed — see probe results below");
                  }
                } catch (e) {
                  const detail = e?.response?.data?.detail || "Test connection failed";
                  toast.error(detail);
                  setFellowProbeResult({ error: detail });
                } finally {
                  setFellowTesting(false);
                }
              }}
              className="bg-rose-500/10 border-rose-500/30 text-rose-200 hover:bg-rose-500/20"
            >
              {fellowTesting ? "Probing…" : "Test connection"}
            </Button>
            <span className="text-xs text-zinc-500">Save the token + subdomain first, then test.</span>
          </div>

          {fellowProbeResult?.diagnosis && !fellowProbeResult?.authenticated && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded p-2.5 text-xs text-amber-100 leading-snug">
              <div className="text-amber-300 text-[10px] uppercase tracking-wider mb-1">Diagnosis</div>
              {fellowProbeResult.diagnosis}
            </div>
          )}

          {fellowProbeResult && (
            <div className="bg-zinc-950/60 border border-zinc-800 rounded p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-medium text-zinc-300">
                  {fellowProbeResult.authenticated ? (
                    <span className="text-emerald-300">✓ Authenticated</span>
                  ) : fellowProbeResult.error ? (
                    <span className="text-red-300">✗ {fellowProbeResult.error}</span>
                  ) : (
                    <span className="text-amber-300">⚠ No probe returned 2xx</span>
                  )}
                </div>
                {fellowProbeResult.next_step && (
                  <div className="text-[11px] text-zinc-500 italic">{fellowProbeResult.next_step}</div>
                )}
              </div>
              {Array.isArray(fellowProbeResult.probes) && (
                <div className="space-y-1.5">
                  {fellowProbeResult.probes.map((p, i) => (
                    <details key={i} className={`text-[11px] border rounded px-2 py-1 ${p.ok ? "bg-emerald-500/5 border-emerald-500/30" : "bg-zinc-900 border-zinc-800"}`}>
                      <summary className="cursor-pointer flex items-center gap-2 hover:text-zinc-200">
                        <span className={`font-mono px-1.5 rounded text-[10px] ${p.ok ? "bg-emerald-500/20 text-emerald-300" : p.status >= 400 ? "bg-red-500/20 text-red-300" : "bg-zinc-700/40 text-zinc-400"}`}>
                          {p.status || "ERR"}
                        </span>
                        <span className="font-mono text-zinc-400">{p.method}</span>
                        <span className="text-zinc-300 truncate">{p.url}</span>
                      </summary>
                      <pre className="mt-2 whitespace-pre-wrap font-mono text-[10px] leading-snug text-zinc-400 max-h-[200px] overflow-y-auto break-all">
                        {p.body_excerpt || "(empty body)"}
                      </pre>
                    </details>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-zinc-600 italic">
                Tip: once you see a 2xx response, copy this whole block into the chat — that's how we'll build the Phase 2 sync against the actual response shape.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Marketing Analytics access — external agency allowlist */}
      <MarketingAnalyticsAccessCard />

      {/* Claude AI Bot key */}
      <ClaudeBotKeyCard />
    </div>
  );
};

// Marker: marketing-analytics-access-config-v1
const MarketingAnalyticsAccessCard = () => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [grantOpen, setGrantOpen] = React.useState(false);
  const [grantEmail, setGrantEmail] = React.useState("");
  const [grantName, setGrantName] = React.useState("");
  const [grantBusy, setGrantBusy] = React.useState(false);
  const [credentialModal, setCredentialModal] = React.useState(null);

  const fetchRows = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/marketing-analytics/access-config");
      setRows(res.data?.rows || []);
    } catch (e) {
      const msg = e?.response?.status === 403
        ? "Admin only"
        : (e?.response?.data?.detail || "Failed to load");
      toast.error(`Marketing Analytics access: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleGrant = async () => {
    if (!grantEmail || !grantEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setGrantBusy(true);
    try {
      const res = await api.post("/marketing-analytics/grant-access", {
        email: grantEmail.trim().toLowerCase(),
        full_name: grantName.trim() || undefined,
      });
      if (res.data?.password) {
        setCredentialModal({
          email: res.data.email,
          password: res.data.password,
          full_name: res.data.full_name,
          is_new: res.data.is_new_record,
          login_url: res.data.login_url,
        });
      } else {
        toast.success(`${res.data.email} added to allowlist. Existing staff record — password unchanged.`);
      }
      setGrantOpen(false);
      setGrantEmail("");
      setGrantName("");
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setGrantBusy(false);
    }
  };

  const handleResetPassword = async (email, fullName) => {
    if (!window.confirm(`Generate a NEW password for ${email}? Old password will stop working.`)) return;
    try {
      const res = await api.post("/marketing-analytics/grant-access", {
        email, full_name: fullName, reset_password: true,
      });
      if (res.data?.password) {
        setCredentialModal({
          email: res.data.email,
          password: res.data.password,
          full_name: res.data.full_name,
          is_new: false,
          login_url: res.data.login_url,
        });
      }
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const handleRevoke = async (email) => {
    if (!window.confirm(`Revoke ${email}'s access to /marketing-analytics? Staff record is preserved (login disabled).`)) return;
    try {
      await api.post("/marketing-analytics/revoke-access", { email });
      toast.success(`Revoked access for ${email}`);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Megaphone className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-lg text-zinc-50">Marketing Analytics access</CardTitle>
                <CardDescription className="text-zinc-400 text-xs">
                  Email allowlist for external marketing-agency users who need to view <code>/marketing-analytics</code> without a portal role
                </CardDescription>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setGrantOpen(true)}
              className="bg-purple-500/10 border border-purple-500/30 text-purple-200 hover:bg-purple-500/20"
            >
              <Plus className="w-4 h-4 mr-1" /> Grant access
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-purple-500/5 border border-purple-500/20 rounded p-3 text-xs text-purple-200/80 leading-relaxed">
            Agency users created here get role <code className="text-purple-200">team</code> + portal access enabled. They see <strong>only</strong> /marketing-analytics (other pages are gated by role). Login at <code>/login</code> with the password shown after grant.
          </div>

          {loading && !rows.length ? (
            <div className="text-sm text-zinc-500 text-center py-4">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-zinc-500 text-center py-6 border border-dashed border-zinc-800 rounded">
              No agency emails granted yet. Click <strong>Grant access</strong> to add the first one.
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.email} className="flex items-center justify-between gap-3 bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-50 truncate">
                      {r.full_name || <span className="text-zinc-500 italic">(no name)</span>}
                      <span className="ml-2 text-zinc-400 font-mono text-[11px]">{r.email}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px]">
                      {r.has_staff_record ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-emerald-500/10 border-emerald-500/30 text-emerald-300">
                          staff record · role={r.role}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-amber-500/10 border-amber-500/30 text-amber-300">
                          no staff record — can't login
                        </span>
                      )}
                      {r.can_login ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-emerald-500/10 border-emerald-500/30 text-emerald-300">
                          ✓ login enabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-zinc-700/40 border-zinc-600 text-zinc-400">
                          login disabled
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => handleResetPassword(r.email, r.full_name)}
                      className="h-7 text-[11px] text-zinc-400 hover:text-cyan-300"
                      title="Generate fresh password"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" /> Reset password
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => handleRevoke(r.email)}
                      className="h-7 text-[11px] text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      title="Remove from allowlist + disable login"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grant-access modal */}
      {grantOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !grantBusy && setGrantOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-semibold text-zinc-50">Grant marketing-analytics access</h3>
              <p className="text-xs text-zinc-400 mt-1">Creates a staff record (role=team, portal access ON) + adds email to allowlist. A random 16-char password is generated and shown ONCE.</p>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Email *</Label>
              <Input
                type="email"
                value={grantEmail}
                onChange={(e) => setGrantEmail(e.target.value)}
                placeholder="agency@partner.com"
                className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Full name (optional)</Label>
              <Input
                value={grantName}
                onChange={(e) => setGrantName(e.target.value)}
                placeholder="Marketing Agency Lead"
                className="bg-zinc-950 border-zinc-800 text-zinc-50 mt-1"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Defaults to the email's local-part if empty.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setGrantOpen(false)} disabled={grantBusy}>Cancel</Button>
              <Button size="sm" onClick={handleGrant} disabled={grantBusy} className="bg-purple-500 hover:bg-purple-600 text-white">
                {grantBusy ? "Granting…" : "Grant access"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* One-shot credential reveal modal */}
      {credentialModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCredentialModal(null)}>
          <div className="bg-zinc-900 border border-emerald-500/40 rounded-lg max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-emerald-300">
              <Key className="w-5 h-5" />
              <h3 className="text-lg font-semibold">Credentials — copy now, shown only once</h3>
            </div>
            <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
              ⚠ This password will <strong>not</strong> be shown again. Save it now and share with the agency over a secure channel.
            </p>
            <div className="bg-zinc-950 border border-zinc-800 rounded p-3 space-y-2 font-mono text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-zinc-500 text-[11px] uppercase tracking-wider">Login URL</span>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { navigator.clipboard.writeText(credentialModal.login_url); toast.success("Copied"); }}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="text-cyan-300 break-all">{credentialModal.login_url}</div>
              <div className="border-t border-zinc-800 pt-2 flex items-center justify-between gap-3">
                <span className="text-zinc-500 text-[11px] uppercase tracking-wider">Email</span>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { navigator.clipboard.writeText(credentialModal.email); toast.success("Copied"); }}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="text-zinc-50">{credentialModal.email}</div>
              <div className="border-t border-zinc-800 pt-2 flex items-center justify-between gap-3">
                <span className="text-zinc-500 text-[11px] uppercase tracking-wider">Password</span>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { navigator.clipboard.writeText(credentialModal.password); toast.success("Copied"); }}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="text-emerald-300 break-all font-bold">{credentialModal.password}</div>
            </div>
            <div className="text-xs text-zinc-400">
              {credentialModal.is_new ? "New staff record created." : "Existing staff record updated — password reset."} Role = <code className="text-zinc-200">team</code>. Sidebar will show only Marketing Analytics + Sales scoring for this user.
            </div>
            <div className="flex justify-end pt-1">
              <Button size="sm" onClick={() => setCredentialModal(null)} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                I saved the password
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IntegrationSettingsPage;
